import { useEffect, useRef } from "react";

// Module-level stack of {title} entries, one per mounted useDocumentMeta
// caller. Needed because detail routes render as modals over a background page
// (see AppRoutes.unwrapChain) — the Explore page and a collection dialog on top
// of it can be mounted simultaneously, and both have a legitimate claim on
// document.title. The topmost (most recently pushed) entry always wins, which
// matches render order: a modal mounts after its background page, and a
// nested modal mounts after that.
const stack = [];

// Captured once, from the static title index.html ships with — this is what
// gets restored once every pushed entry has been popped again, e.g. closing
// the last open dialog and landing back on a page with no useDocumentMeta
// call of its own.
const defaultTitle = document.title;

function apply() {
  const top = stack[stack.length - 1];
  document.title = top?.title || defaultTitle;
}

/**
 * Sets the browser tab title while the calling component is mounted,
 * restoring whatever was showing beneath it on unmount. Pass {} or omit title
 * before data has loaded — an entry with no title yet simply doesn't override
 * whatever's already showing.
 *
 * Has no effect on link unfurls; crawlers never run client JS. That's handled
 * server-side by client/api/unfurl.js against the backend's /meta/* routes.
 */
export function useDocumentMeta({ title } = {}) {
  const entryRef = useRef({ title });

  // Mount-only: push this call's slot onto the stack before anything reads it,
  // and remove it BY IDENTITY on unmount — not by a captured array index, which
  // would go stale under React 18 StrictMode's dev-only double mount/unmount.
  // Removing by identity is also what makes closing a modal correctly restore
  // whatever entry sits beneath it: that entry's own slot was never touched.
  useEffect(() => {
    const entry = entryRef.current;
    stack.push(entry);
    apply();
    return () => {
      const index = stack.indexOf(entry);
      if (index !== -1) stack.splice(index, 1);
      apply();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs whenever title changes — the path that fires once a query resolves
  // and the real copy arrives. Mutates this call's existing stack slot in
  // place (same object the mount effect pushed) rather than pushing a new
  // one, so re-applying only changes the tab title when this is still the
  // topmost entry.
  useEffect(() => {
    entryRef.current.title = title;
    apply();
  }, [title]);
}
